/**
 * @file: SearchResultRow.tsx
 *
 * One chat-search result row — a thread (avatar + title + preview + unread)
 * or a contact (avatar + name + title) — plus the thin list separator.
 * Presentational; the page supplies the press handlers.
 */

import { Typography } from '@euler/app/components/typography';
import { chatThreadAvatar } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { Avatar } from '@euler/components/Avatar';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatContact, ChatThreadItem } from '@euler/model/chat';
import { FC, memo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { SearchResult } from '../functions/useChatSearch';

export const SearchResultRow: FC<{
  item: SearchResult;
  onThreadPress: (thread: ChatThreadItem, displayName: string) => void;
  onContactPress: (contact: ChatContact) => void;
}> = memo(({ item, onThreadPress, onContactPress }) => {
  const { rpx } = useResponsiveContext();

  if (item.kind === 'thread') {
    const t = item.data;
    const hasUnread = t.unreadCount > 0;
    return (
      <TouchableOpacity
        onPress={() => onThreadPress(t, item.displayName)}
        activeOpacity={0.6}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: rpx(12),
          paddingHorizontal: rpx(16),
          backgroundColor: '#fff',
        }}
      >
        <Avatar
          {...chatThreadAvatar(t)}
          textStyle={{
            fontFamily: FontFamily.NotoSans.Medium,
            fontSize: rpx(16),
            lineHeight: rpx(20),
          }}
          style={{
            width: rpx(44),
            height: rpx(44),
            borderRadius: rpx(22),
            marginRight: rpx(12),
          }}
        />
        <View style={{ flex: 1 }}>
          <Typography.Text
            size={rpx(15)}
            color="#111"
            style={{
              fontFamily: hasUnread
                ? FontFamily.NotoSans.Bold
                : FontFamily.NotoSans.Medium,
            }}
          >
            {item.displayName}
          </Typography.Text>
          {t.latestMessage?.preview ? (
            <Typography.Text
              size={rpx(12)}
              color="#9ca3af"
              numberOfLines={1}
              style={{ marginTop: rpx(2) }}
            >
              {t.latestMessage.preview}
            </Typography.Text>
          ) : null}
        </View>
        {hasUnread ? <UnreadBadge count={t.unreadCount} /> : null}
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      onPress={() => onContactPress(item.data)}
      activeOpacity={0.6}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: rpx(12),
        paddingHorizontal: rpx(16),
        backgroundColor: '#fff',
      }}
    >
      <Avatar
        uri={item.data.avatar ?? undefined}
        name={item.data.name}
        textStyle={{
          fontFamily: FontFamily.NotoSans.Medium,
          fontSize: rpx(16),
          lineHeight: rpx(20),
        }}
        style={{
          width: rpx(44),
          height: rpx(44),
          borderRadius: rpx(22),
          marginRight: rpx(12),
        }}
      />
      <View style={{ flex: 1 }}>
        <Typography.Text
          size={rpx(15)}
          color="#111"
          style={{ fontFamily: FontFamily.NotoSans.Medium }}
        >
          {item.data.name}
        </Typography.Text>
        {item.data.jobTitle ? (
          <Typography.Text
            size={rpx(12)}
            color="#9ca3af"
            style={{ marginTop: rpx(2) }}
          >
            {item.data.jobTitle}
          </Typography.Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
});

export const SearchResultSeparator = memo(() => (
  <View
    style={{
      height: StyleSheet.hairlineWidth,
      backgroundColor: '#f0f0f0',
      marginLeft: 70,
    }}
  />
));
